import { useState } from 'react';

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';

import { adminApi, type AdminTenant } from '@/api/queries/admin';
import { isSuperAdmin, useTenantStore } from '@/auth/tenantStore';
import { ErrorComponent } from '@/components/common/ErrorComponent';
import { useSetBreadcrumbs } from '@/components/layout/breadcrumbs';
import { INVITE_ROLE_HINT } from '@/components/team/inviteRoleHint';
import {
  Button,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  StatusPill,
  Table,
  useToast,
  type StatusTone,
  type TableColumn,
} from '@/components/ui';
import { formatDate } from '@/utils/formatDate';

function tenantStatusTone(status: string): StatusTone {
  if (status === 'active') return 'active';
  if (status === 'archived') return 'dnc';
  return 'inactive';
}

function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function AdminTenantsRoute() {
  useSetBreadcrumbs([{ label: 'Admin' }, { label: 'Tenants' }]);
  const navigate = useNavigate();
  const { data: tenants, isPending } = adminApi.useTenants();
  const [createOpen, setCreateOpen] = useState(false);

  const columns: TableColumn<AdminTenant>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (t) => <span style={{ fontWeight: 500 }}>{t.name}</span>,
    },
    {
      id: 'slug',
      header: 'Slug',
      cell: (t) => <code style={{ fontSize: 12, color: 'var(--text-3)' }}>{t.slug}</code>,
      width: 200,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (t) => (
        <StatusPill status={tenantStatusTone(t.status)}>{statusLabel(t.status)}</StatusPill>
      ),
      width: 140,
    },
    {
      id: 'updatedAt',
      header: 'Last updated',
      cell: (t) => formatDate(t.updatedAt) ?? '—',
      width: 160,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Tenants</h1>
        <Button
          variant="primary"
          leftIcon={<Icon name="plus" size={14} />}
          onClick={() => setCreateOpen(true)}
        >
          New tenant
        </Button>
      </div>

      <Table
        columns={columns}
        rows={tenants ?? []}
        rowKey={(t) => t.id}
        loading={isPending}
        emptyMessage="No tenants yet."
        onRowClick={(t) =>
          navigate({
            to: '/admin/tenants/$tenantId',
            params: { tenantId: t.id },
            search: { tab: 'overview' },
          })
        }
      />

      {createOpen && <CreateTenantModal onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

function CreateTenantModal({ onClose }: { onClose: () => void }) {
  const create = adminApi.useCreateTenant();
  const invite = adminApi.useInviteMember();
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [working, setWorking] = useState(false);

  const updateMember = (index: number, email: string) =>
    setMembers((prev) => prev.map((m, i) => (i === index ? email : m)));

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setWorking(true);
    try {
      const tenant = await create.mutateAsync({ name: trimmed, slug: slug.trim() || undefined });

      // Invite sequentially so one bad/duplicate email doesn't abort the rest; the
      // tenant already exists at this point, so we surface per-row failures and still
      // land the operator on the tenant.
      const pending = members.map((m) => m.trim()).filter(Boolean);
      let invited = 0;
      const failures: string[] = [];
      for (const email of pending) {
        try {
          await invite.mutateAsync({ tenantId: tenant.id, email });
          invited += 1;
        } catch (err) {
          const conflict = isAxiosError(err) && err.response?.status === 409;
          failures.push(
            conflict
              ? `${email} is already an active member`
              : `${email}: ${err instanceof Error ? err.message : 'invite failed'}`,
          );
        }
      }

      toast.show({
        title: `Tenant "${trimmed}" created${invited ? `, ${invited} member(s) invited` : ''}`,
        description: failures.length ? failures.join('; ') : undefined,
        severity: failures.length ? 'error' : 'success',
      });
      onClose();
      navigate({
        to: '/admin/tenants/$tenantId',
        params: { tenantId: tenant.id },
        search: { tab: 'members' },
      });
    } catch (err) {
      toast.show({
        title: 'Failed to create tenant',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    } finally {
      setWorking(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="New tenant"
      size="md"
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose} disabled={working}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={working} disabled={!name.trim()}>
            Create
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme"
            autoFocus
          />
        </Field>
        <Field label="Slug" hint="Leave blank to auto-generate from the name.">
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme" />
        </Field>

        <Field label="Initial members" hint={`Optional. ${INVITE_ROLE_HINT}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {members.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input
                  type="email"
                  value={m}
                  onChange={(e) => updateMember(i, e.target.value)}
                  placeholder="someone@example.com"
                  style={{ flex: 1 }}
                />
                <IconButton
                  icon="trash"
                  label="Remove member"
                  size="sm"
                  onClick={() => setMembers((prev) => prev.filter((_, idx) => idx !== i))}
                />
              </div>
            ))}
            <div>
              <Button
                variant="ghost"
                leftIcon={<Icon name="plus" size={14} />}
                onClick={() => setMembers((prev) => [...prev, ''])}
              >
                Add member
              </Button>
            </div>
          </div>
        </Field>
      </div>
    </Modal>
  );
}

export const Route = createFileRoute('/admin/tenants/')({
  beforeLoad: () => {
    if (!isSuperAdmin(useTenantStore.getState())) {
      throw redirect({ to: '/contacts', search: { page: 0, pageSize: 25 } });
    }
  },
  component: AdminTenantsRoute,
  errorComponent: ErrorComponent,
});
